import { useRef } from "react";

import { Button, Persona, Text } from "@fluentui/react-components";
import { PenFilled } from "@fluentui/react-icons";
import { devices } from "library/constants";
import { ETaxonomy, IDependent } from "library/types";
import { formatFile } from "library/utils";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styled from "styled-components";

import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { Flex } from "components/styled";
import { UploadIcon } from "components/UploadIcon/UploadIcon";
import { useAppTheme } from "hooks/useAppTheme";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { useUpdateDependent } from "state/useUpdateDependent";

type Props = {
  fullView?: boolean;
  dependentData?: IDependent;
  setEditDependent: (dependent: IDependent) => void;
};

export function DependentProfile({ dependentData, fullView, setEditDependent }: Props) {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dependentTypeOptions = useTaxonomyOptions(ETaxonomy.DependentType);

  const { mutate: onUpdateDependent, isLoading: updateInProgress } = useUpdateDependent(false);

  const handleUpdatePhoto = async (event) => {
    const file = event.target.files[0];
    const formattedFile = await formatFile(file);

    onUpdateDependent(
      {
        countryCode: Number(dependentData?.countryCode),
        clientInternalNumberGOS: Number(dependentData?.clientInternalNumberGos),
        employeeDependentTag: Number(dependentData?.employeeDependentTag),
        payload: {
          ...dependentData,
          backgroundPictureContent: formattedFile?.base64String || "",
          backgroundPictureName: formattedFile?.name || "",
          backgroundPictureMimeType: formattedFile?.type || "",
          backgroundPicturePath: formattedFile?.path || "",
        } as IDependent,
      },
      {
        onSuccess() {
          toast.success(t("hbh.dependentUpdatedSuccessfully.label"));
        },
      }
    );
  };

  const initDependentType = dependentTypeOptions.find(
    (option) => option.value === dependentData?.dependentType
  );

  return fullView ? (
    <DependentDetailsWrap>
      {updateInProgress && <AppLogoSpinner />}

      {dependentData?.backgroundPicturePath ? (
        <img src={dependentData?.backgroundPicturePath} alt="user-profile" />
      ) : (
        <NoImagePlaceholder>{`${dependentData?.firstName?.charAt(0)}${dependentData?.lastName?.charAt(0)}`}</NoImagePlaceholder>
      )}
      <Button
        style={{
          position: "absolute",
          left: "80px",
          bottom: "10px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
        }}
        onClick={() => fileInputRef.current?.click()}
        shape="circular"
        appearance="primary"
        icon={
          <>
            <UploadIcon fill={theme.palette.white} />
            <input ref={fileInputRef} onChange={handleUpdatePhoto} type="file" accept="*" hidden />
          </>
        }
      />
      <InfoWrap>
        <Flex>
          <Text
            style={{
              fontSize: "22px",
            }}
            weight="medium"
          >
            {`${dependentData?.firstName} ${dependentData?.lastName}`}
          </Text>
        </Flex>
        <Flex>
          <Text
            style={{
              fontSize: "20",
            }}
            weight="medium"
          >
            {initDependentType?.text}
          </Text>
        </Flex>
      </InfoWrap>
    </DependentDetailsWrap>
  ) : (
    <ListViewWrap>
      <Persona
        name={`${dependentData?.firstName} ${dependentData?.lastName}`}
        avatar={{
          image: {
            src: dependentData?.backgroundPicturePath || "",
          },
        }}
      />
      <Button
        appearance="transparent"
        onClick={() => {
          setEditDependent(dependentData as IDependent);
        }}
        icon={
          <PenFilled
            fontSize={12}
            style={{
              color: theme.palette.purpleDark,
            }}
          />
        }
      />
    </ListViewWrap>
  );
}

const ListViewWrap = styled.div`
  display: flex;
  margin: 5px 0;
  gap: 20px;
  align-items: center;
  padding-bottom: 5px;
  .fui-Persona__primaryText {
    display: flex;
    align-items: center;
    height: 31px;
    min-width: 150px;
  }
  @media only screen and ${devices.md} {
    width: 500px;
  }
`;

const DependentDetailsWrap = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  margin-top: 20px;
  img {
    width: 110px;
    height: 110px;
    border: 2px solid ${({ theme }) => theme.palette.white};
    background: ${({ theme }) => theme.palette.neutralLighterAlt};
    border-radius: 50%;
  }
  @media only screen and ${devices.md} {
  }
`;

const NoImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  justify-content: center;
  font-weight: 600;

  width: 110px;
  height: 110px;
  border: 2px solid ${({ theme }) => theme.palette.white};
  background: ${({ theme }) => theme.palette.neutralLighterAlt};
  border-radius: 50%;
`;

const InfoWrap = styled.div`
  padding: 10px 20px 20px;
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 7px;
  flex-direction: column;
`;
